import { Button } from "@shared/components/ui/Button";
import { useImageUpload } from "@shared/hooks/useImageUpload";
import { OrganizationProfile } from "@shared/types/organization";
import theme from "@shared/utils/themeColors";
import React, { useState } from "react";
import { toast } from "react-hot-toast";
import ProfileBg from "../../../images/profile-bg.jpg";
import { getOrganizationLogo } from "../utils/organization.utils";

interface OrganizationProfileCardProps {
  profile: OrganizationProfile;
  onRefresh?: () => Promise<void>;
  setProfileSidebarOpen?: (open: boolean) => void;
  profileSidebarOpen?: boolean;
}

export const OrganizationProfileCard: React.FC<
  OrganizationProfileCardProps
> = ({
  profile,
  onRefresh,
  setProfileSidebarOpen = undefined,
  profileSidebarOpen = false,
}) => {
  const {
    name,
    description,
    industry,
    location,
    website,
    founded,
    size,
    city,
    state,
    country,
    streetAddress,
    postalCode,
    type,
    receiveNotifications,
  } = profile;

  const { uploadImage, isUploading } = useImageUpload({
    type: "background",
    onSuccess: () => {
      toast.success("Background image updated");
    },
    onError: (error) => {
      toast.error("Error updating background image");
      console.error("Error uploading background:", error);
    },
  });

  const handleBackgroundUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      await uploadImage(file);
      // Refetch profile data after successful upload
      if (onRefresh) await onRefresh();
    } catch (error) {
      console.error("Error handling background upload:", error);
    }
  };

  const hasSidebar = setProfileSidebarOpen !== undefined;

  const handleBack = () => {
    if (hasSidebar) {
      setProfileSidebarOpen(true);
      return;
    }
    window.history.back();
  };

  const [activeTab, setActiveTab] = useState<"about" | "details">("details");

  // Tabs navigation
  const tabs = [
    { id: "details", label: "Details" },
    /* { id: "about", label: "About" }, */
  ];

  return (
    <div
      className={`grow flex flex-col md:translate-x-0 transition-transform duration-300 ease-in-out ${
        profileSidebarOpen ? "translate-x-1/3" : "translate-x-0"
      }`}
    >
      {/* Profile background */}
      <div className="relative h-56 bg-gray-200 dark:bg-gray-900">
        <img
          className="object-cover h-full w-full"
          src={ProfileBg}
          width="979"
          height="220"
          alt="Profile background"
        />
        {/* Close button */}
        <button
          className="absolute top-4 left-4 sm:left-6 text-white opacity-80 hover:opacity-100"
          aria-controls="profile-sidebar"
          type="button"
          onClick={handleBack}
        >
          <span className="sr-only">Close sidebar</span>
          <svg
            className="w-6 h-6 fill-current"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M10.7 18.7l1.4-1.4L7.8 13H20v-2H7.8l4.3-4.3-1.4-1.4L4 12z" />
          </svg>
        </button>

        {isUploading && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="relative px-4 sm:px-6 pb-8">
        {/* Pre-header */}
        <div className="-mt-16 mb-6 sm:mb-3">
          <div className="flex flex-col items-center sm:flex-row sm:justify-between sm:items-end">
            {/* Avatar */}
            <div className="inline-flex -ml-1 -mt-1 mb-4 sm:mb-0">
              <img
                className="rounded-lg w-auto h-24"
                src={getOrganizationLogo(profile)}
                alt="Organization Logo"
              />
            </div>

            {/* Actions */}
            <div className="flex space-x-2 sm:mb-2">
              {website && (
                <Button
                  href={
                    website.startsWith("http") ? website : `https://${website}`
                  }
                  as="a"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Visit Website
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Header */}
        <header className="text-center sm:text-left mb-6">
          {/* Name */}
          <div className="inline-flex items-start mb-2">
            <h1 className="text-2xl md:text-3xl font-bold">{name}</h1>
          </div>
          {/* Bio */}
          {industry && (
            <div className="text-sm mb-3">
              {industry} {founded && `• Founded ${founded}`}{" "}
              {size && `• ${size} employees`}
            </div>
          )}
          {/* Meta */}
          {location && (
            <div className="flex flex-wrap justify-center sm:justify-start space-x-4">
              <div className="flex items-center">
                <svg
                  className="w-4 h-4 fill-current shrink-0 text-gray-400 dark:text-gray-500"
                  viewBox="0 0 16 16"
                >
                  <path d="M8 8.992a2 2 0 1 1-.002-3.998A2 2 0 0 1 8 8.992Zm-.7 6.694c-.1-.1-4.2-3.696-4.2-3.796C1.7 10.69 1 8.892 1 6.994 1 3.097 4.1 0 8 0s7 3.097 7 6.994c0 1.898-.7 3.697-2.1 4.996-.1.1-4.1 3.696-4.2 3.796-.4.3-1 .3-1.4-.1Zm-2.7-4.995L8 13.688l3.4-2.997c1-1 1.6-2.198 1.6-3.597 0-2.798-2.2-4.996-5-4.996S3 4.196 3 6.994c0 1.399.6 2.698 1.6 3.697 0-.1 0-.1 0 0Z" />
                </svg>
                <div className="ml-2 whitespace-nowrap">{location}</div>
              </div>
            </div>
          )}
        </header>

        {/* Tabs */}
        <div className="relative mb-6">
          <div
            className="absolute bottom-0 w-full h-px bg-gray-200 dark:bg-gray-700"
            aria-hidden="true"
          ></div>
          <ul className="relative text-sm font-medium flex flex-nowrap -mx-4 sm:-mx-6 lg:-mx-8 overflow-x-scroll no-scrollbar">
            {tabs.map((tab) => (
              <li
                key={tab.id}
                className="mr-6 last:mr-0 first:pl-4 sm:first:pl-6 lg:first:pl-8 last:pr-4 sm:last:pr-6 lg:last:pr-8"
              >
                <button
                  className={`block pb-3 ${
                    activeTab === tab.id
                      ? theme.text.primary
                      : "text-gray-400 dark:text-gray-600 hover:text-gray-500 dark:hover:text-gray-400"
                  } whitespace-nowrap`}
                  onClick={() => setActiveTab(tab.id as "about" | "details")}
                >
                  {tab.label}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* About tab panel */}
        <div
          className={`${activeTab !== "about" && "hidden"}`}
          role="tabpanel"
          id="about-tab"
        >
          <div className="text-sm space-y-6">
            <div>
              <h3 className="font-semibold mb-2">About</h3>
              <div className="space-y-2">
                <p>{description || "No description provided."}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Details tab panel */}
        <div
          className={`${activeTab !== "details" && "hidden"}`}
          role="tabpanel"
          id="details-tab"
        >
          <div className="text-sm space-y-6">
            <div>
              <h3 className="font-semibold mb-2">Organization Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {industry && (
                  <div>
                    <span className="font-medium">Industry:</span> {industry}
                  </div>
                )}
                {size && (
                  <div>
                    <span className="font-medium">Size:</span> {size}
                  </div>
                )}
                {founded && (
                  <div>
                    <span className="font-medium">Founded:</span> {founded}
                  </div>
                )}
                {type && (
                  <div>
                    <span className="font-medium">Type:</span> {type}
                  </div>
                )}
                {website && (
                  <div>
                    <span className="font-medium">Website:</span>{" "}
                    <a
                      href={
                        website.startsWith("http")
                          ? website
                          : `https://${website}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:underline"
                    >
                      {website}
                    </a>
                  </div>
                )}
                {receiveNotifications !== undefined && (
                  <div>
                    <span className="font-medium">Receive Notifications:</span>{" "}
                    {receiveNotifications ? "Yes" : "No"}
                  </div>
                )}
              </div>

              <h3 className="font-semibold mt-6 mb-2">Location Information</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {location && (
                  <div className="col-span-1 sm:col-span-2">
                    <span className="font-medium">Location:</span> {location}
                  </div>
                )}
                {streetAddress && (
                  <div>
                    <span className="font-medium">Street Address:</span>{" "}
                    {streetAddress}
                  </div>
                )}
                {city && (
                  <div>
                    <span className="font-medium">City:</span> {city}
                  </div>
                )}
                {state && (
                  <div>
                    <span className="font-medium">State/Province:</span> {state}
                  </div>
                )}
                {country && (
                  <div>
                    <span className="font-medium">Country:</span> {country}
                  </div>
                )}
                {postalCode && (
                  <div>
                    <span className="font-medium">Postal Code:</span>{" "}
                    {postalCode}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
